<template>
  <div class="otp-input-container d-flex justify-content-center" @paste="onPaste">
    <input
      v-for="(digit, index) in digits"
      :key="index"
      :ref="'otpDigit' + index"
      type="text"
      inputmode="numeric"
      maxlength="1"
      class="otp-digit"
      :class="{ 'otp-digit--filled': digit, 'otp-digit--error': hasError }"
      :value="digit"
      :disabled="disabled"
      :data-cy="'otp-digit-' + index"
      @input="onInput($event, index)"
      @keydown="onKeydown($event, index)"
      @focus="onFocus(index)"
    />
  </div>
</template>

<script>
const OTP_LENGTH = 6;

export default {
  name: 'OtpInput',
  props: {
    value: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    hasError: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      digits: Array(OTP_LENGTH).fill(''),
      internalUpdate: false
    };
  },
  watch: {
    value(newVal) {
      if (this.internalUpdate) return;
      if (!newVal) {
        this.digits = Array(OTP_LENGTH).fill('');
        return;
      }
      this.setDigitsFromString(newVal);
    }
  },
  methods: {
    setDigitsFromString(str) {
      const clean = str.replace(/\D/g, '').slice(0, OTP_LENGTH);
      this.digits = Array(OTP_LENGTH).fill('').map((_, i) => clean[i] || '');
      this.emitValue();
    },
    emitValue() {
      const code = this.digits.join('');
      this.internalUpdate = true;
      this.$emit('input', code);
      if (code.length === OTP_LENGTH) {
        this.$emit('complete', code);
      }
      this.$nextTick(() => {
        this.internalUpdate = false;
      });
    },
    onInput(event, index) {
      const char = event.data || event.target.value;
      const digit = (char || '').replace(/\D/g, '').slice(-1);

      this.$set(this.digits, index, digit);
      this.emitValue();

      if (digit && index < OTP_LENGTH - 1) {
        this.focusDigit(index + 1);
      }
    },
    onKeydown(event, index) {
      if (event.key === 'Backspace') {
        if (!this.digits[index] && index > 0) {
          this.$set(this.digits, index - 1, '');
          this.emitValue();
          this.focusDigit(index - 1);
          event.preventDefault();
        } else {
          this.$set(this.digits, index, '');
          this.emitValue();
        }
      }

      if (event.key === 'ArrowLeft' && index > 0) {
        this.focusDigit(index - 1);
        event.preventDefault();
      }

      if (event.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
        this.focusDigit(index + 1);
        event.preventDefault();
      }
    },
    onPaste(event) {
      event.preventDefault();
      const pasted = (event.clipboardData || window.clipboardData).getData('text');
      this.setDigitsFromString(pasted);

      const filled = pasted.replace(/\D/g, '').length;
      const focusIndex = Math.min(filled, OTP_LENGTH - 1);
      this.focusDigit(focusIndex);
    },
    onFocus(index) {
      const input = this.$refs['otpDigit' + index];
      if (input && input[0]) {
        input[0].select();
      }
    },
    focusDigit(index) {
      this.$nextTick(() => {
        const input = this.$refs['otpDigit' + index];
        if (input && input[0]) {
          input[0].focus();
          input[0].select();
        }
      });
    },
    focusFirst() {
      this.focusDigit(0);
    }
  }
};
</script>

<style scoped>
.otp-input-container {
  gap: 8px;
}

.otp-digit {
  width: 48px;
  height: 56px;
  text-align: center;
  font-size: 1.5rem;
  font-weight: 600;
  border: 2px solid #d8d6de;
  border-radius: 8px;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
  background-color: #fff;
  caret-color: #7367f0;
}

.otp-digit:focus {
  border-color: #7367f0;
  box-shadow: 0 0 0 0.2rem rgba(115, 103, 240, 0.25);
}

.otp-digit--filled {
  border-color: #7367f0;
}

.otp-digit--error {
  border-color: #ea5455;
}

.otp-digit--error:focus {
  border-color: #ea5455;
  box-shadow: 0 0 0 0.2rem rgba(234, 84, 85, 0.25);
}

.otp-digit:disabled {
  background-color: #f8f8f8;
  cursor: not-allowed;
}
</style>
